import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tag, Plus, Trash2, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "./utils";

const DiscountCodes = () => {
  const { token } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    percentage: 20,
    label: "",
    usage_limit: "",
    expires_at: "",
    note: "",
  });
  const [testCode, setTestCode] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testChecking, setTestChecking] = useState(false);

  const runTest = async () => {
    const code = testCode.trim();
    if (!code) return;
    setTestChecking(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/discount/validate`, { code });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ valid: false, error: err.response?.data?.detail || "Fehler" });
    } finally {
      setTestChecking(false);
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCodes(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!form.code || !form.label) {
      toast.error("Code und Label sind Pflicht");
      return;
    }
    try {
      const body = { ...form, stackable_with_prelaunch: false };
      if (!body.usage_limit) delete body.usage_limit;
      else body.usage_limit = parseInt(body.usage_limit, 10);
      if (!body.expires_at) delete body.expires_at;
      if (!body.note) delete body.note;
      await axios.post(`${API}/admin/discount-codes`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Code erstellt");
      setShowForm(false);
      setForm({
        code: "",
        percentage: 20,
        label: "",
        usage_limit: "",
        expires_at: "",
        note: "",
      });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const toggleActive = async (code, active) => {
    try {
      await axios.patch(
        `${API}/admin/discount-codes/${code}`,
        { active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAll();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Code "${code}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Gelöscht");
      fetchAll();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  return (
    <section data-testid="discounts-section" className="space-y-6">
      <div className="border border-white/10 bg-[#0A0A0A] p-6">
        <div className="flex items-center gap-3 mb-3">
          <Tag className="w-5 h-5 text-[#00A651]" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Rabattcodes
          </h2>
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="discount-toggle-form"
            className="ml-auto inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#00C961] text-black font-condensed uppercase tracking-widest text-xs px-4 py-2 transition-colors"
          >
            <Plus className="w-3 h-3" />
            {showForm ? "Schließen" : "Neuer Code"}
          </button>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          Eigene Rabattcodes erstellen — beliebige Prozent (1–99 %), Limit + Ablaufdatum optional.
          Code-Nutzung wird automatisch getrackt.
        </p>

        {showForm && (
          <div
            className="border border-white/10 bg-black p-5 mb-5 space-y-3"
            data-testid="discount-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Code
                </span>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setForm({ ...form, code: e.target.value.toUpperCase() })
                  }
                  placeholder="z.B. SOMMER20"
                  data-testid="discount-code-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm font-mono mt-1"
                />
              </label>
              <label className="block">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Rabatt in %
                </span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={form.percentage}
                  onChange={(e) =>
                    setForm({ ...form, percentage: parseInt(e.target.value) || 0 })
                  }
                  placeholder="z.B. 20"
                  data-testid="discount-percentage-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm mt-1"
                />
                <span className="block text-white/40 text-[10px] mt-1">
                  Beliebiger Wert zwischen 1 und 99 %
                </span>
              </label>
              <label className="block md:col-span-2">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Label (intern)
                </span>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="z.B. Sommer-Aktion 2026"
                  data-testid="discount-label-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm mt-1"
                />
              </label>
              <label className="block">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Limit (optional)
                </span>
                <input
                  type="number"
                  min={1}
                  value={form.usage_limit}
                  onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                  placeholder="leer = unbegrenzt"
                  data-testid="discount-limit-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm mt-1"
                />
              </label>
              <label className="block">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Gültig bis (optional)
                </span>
                <input
                  type="date"
                  value={form.expires_at?.slice(0, 10) || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expires_at: e.target.value ? e.target.value + "T23:59:59Z" : "",
                    })
                  }
                  data-testid="discount-expires-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm mt-1"
                />
              </label>
              <label className="block md:col-span-2">
                <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                  Notiz (intern)
                </span>
                <input
                  type="text"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="z.B. Influencer XY · Geschenk an Kunde"
                  data-testid="discount-note-input"
                  className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm mt-1"
                />
              </label>
            </div>
            <button
              onClick={create}
              data-testid="discount-create-btn"
              className="bg-[#00A651] hover:bg-[#00C961] text-black font-condensed uppercase tracking-widest text-xs px-5 py-2 transition-colors"
            >
              Code erstellen
            </button>
          </div>
        )}

        <div className="border border-white/10 bg-black p-4 mb-4 text-xs text-white/60">
          <strong className="text-[#00A651]">Hinweis:</strong> Erstellte Codes gelten sofort und werden vom Kunden im Warenkorb eingegeben.
          Beim Bundle-Kauf (Handschuh + Glove Bag) wird zusätzlich automatisch <strong className="text-white">CHF 5 Bundle-Rabatt</strong> im Warenkorb abgezogen (unabhängig von einem Code).
        </div>

        {/* Code Tester */}
        <div className="border border-white/10 bg-black p-4 mb-4" data-testid="discount-tester">
          <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
            Code testen (Diagnose)
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={testCode}
              onChange={(e) => {
                setTestCode(e.target.value.toUpperCase());
                setTestResult(null);
              }}
              placeholder="Code eingeben (z.B. SOMMER20)"
              data-testid="discount-test-input"
              className="flex-1 bg-[#0A0A0A] border border-white/15 focus:border-[#00A651] outline-none px-3 py-2 text-white text-sm font-mono uppercase tracking-wider"
              onKeyDown={(e) => e.key === "Enter" && runTest()}
            />
            <button
              onClick={runTest}
              disabled={testChecking || !testCode.trim()}
              data-testid="discount-test-btn"
              className="px-4 font-condensed uppercase tracking-widest text-xs border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black disabled:opacity-40 transition-colors flex items-center gap-2"
            >
              {testChecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Prüfen"}
            </button>
          </div>
          {testResult && (
            <div
              data-testid="discount-test-result"
              className={`mt-3 flex items-center gap-2 text-xs font-condensed uppercase tracking-widest px-3 py-2 ${
                testResult.valid
                  ? "bg-[#00A651]/15 border border-[#00A651]/40 text-[#00A651]"
                  : "bg-red-500/10 border border-red-500/40 text-red-400"
              }`}
            >
              {testResult.valid ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Gültig · {testResult.label} · {testResult.percentage}%
                </>
              ) : (
                <>
                  <XCircle className="w-3.5 h-3.5" />
                  Ungültig · Code nicht gefunden oder abgelaufen
                </>
              )}
            </div>
          )}
        </div>

        {codes.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/40 text-sm">
            {loading ? "Lade..." : 'Noch keine eigenen Codes. Klick oben auf "Neuer Code".'}
          </div>
        ) : (
          <div className="space-y-2" data-testid="discount-list">
            {codes.map((c) => {
              const used = c.times_used || 0;
              const limit = c.usage_limit;
              const expired = c.expires_at && new Date(c.expires_at) < new Date();
              const limitReached = limit && used >= limit;
              const isActive = c.active && !expired && !limitReached;
              return (
                <article
                  key={c.code}
                  className={`border ${
                    isActive ? "border-white/10" : "border-white/5 opacity-60"
                  } bg-black p-4`}
                  data-testid={`discount-row-${c.code}`}
                >
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="font-mono text-white text-base font-bold">
                          {c.code}
                        </code>
                        <span className="text-[#00A651] font-display text-lg">
                          {c.percentage}%
                        </span>
                        {!isActive && (
                          <span className="bg-red-500/20 text-red-400 text-[10px] font-condensed uppercase tracking-widest px-2 py-0.5">
                            {expired
                              ? "Abgelaufen"
                              : limitReached
                              ? "Limit erreicht"
                              : "Inaktiv"}
                          </span>
                        )}
                      </div>
                      <div className="text-white/80 text-sm">{c.label}</div>
                      {c.note && (
                        <div className="text-white/40 text-xs mt-1">{c.note}</div>
                      )}
                      <div className="text-white/40 text-xs mt-2 flex flex-wrap gap-3">
                        <span>
                          Verwendet: <strong className="text-white">{used}</strong>
                          {limit ? `/${limit}` : ""}
                        </span>
                        {c.expires_at && (
                          <span>
                            Gültig bis:{" "}
                            <strong className="text-white">
                              {new Date(c.expires_at).toLocaleDateString("de-CH")}
                            </strong>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => toggleActive(c.code, !c.active)}
                        data-testid={`discount-toggle-${c.code}`}
                        className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 transition-colors ${
                          c.active
                            ? "border border-white/15 hover:border-yellow-400 hover:text-yellow-400 text-white/70"
                            : "border border-[#00A651] text-[#00A651] hover:bg-[#00A651] hover:text-black"
                        }`}
                      >
                        {c.active ? "Deaktivieren" : "Aktivieren"}
                      </button>
                      <button
                        onClick={() => remove(c.code)}
                        data-testid={`discount-delete-${c.code}`}
                        className="p-2 text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default DiscountCodes;
